import { useEffect, useState } from "react";
import { useFiltroEmpresa, SemVinculoToggle } from "@/components/EmpresaSelector";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatusBadge, Modal, EmptyState, Progress, formatDate } from "@/components/pcp";
import { Plus, Eye, Pencil, TrashSimple, MapPin, Flask, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = {
  contratante: "", tipo_contrato: "Público", rodovia: "", trecho: "", municipio: "",
  data_emissao: "", data_inicio_producao: "", prazo_conclusao: "", valor: 0, status: "Pendente",
  google_drive_link: "", observacoes: "", incluir_sabado: false, incluir_domingo: false,
  empresa_executora_id: "",
};

export default function Ordens() {
  const nav = useNavigate();
  const [ordens, setOrdens] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [equipes, setEquipes] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [contratoFilter, setContratoFilter] = useState("");
  const [composicoes, setComposicoes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [servicos, setServicos] = useState([]); // itens a criar junto com a OS
  const [compSel, setCompSel] = useState("");
  const [qtdSel, setQtdSel] = useState("");

  const load = () => api.get("/ordens").then((r) => setOrdens(r.data));

  useEffect(() => {
    load();
    api.get("/equipes").then((r) => setEquipes(r.data));
    api.get("/contratos").then((r) => setContratos(r.data));
    api.get("/composicoes").then((r) => setComposicoes(r.data));
    api.get("/empresas").then((r) => setEmpresas(r.data)).catch(() => setEmpresas([]));
  }, []);

  const openNew = () => { setForm(empty); setEditId(null); setServicos([]); setCompSel(""); setQtdSel(""); setModal(true); };
  const openEdit = (o) => {
    setForm({ ...empty, ...o, valor: o.valor || 0 });
    setEditId(o.id);
    setServicos([]);
    setModal(true);
  };

  const addServico = () => {
    if (!compSel || !qtdSel || parseFloat(qtdSel) <= 0) {
      toast.error("Selecione a composição e informe a quantidade");
      return;
    }
    const c = composicoes.find((x) => x.id === compSel);
    if (!c) return;
    setServicos([...servicos, {
      composicao_id: c.id,
      categoria: c.categoria,
      descricao: c.descricao,
      unidade: c.unidade,
      cor: c.cor || "",
      qtd_planejada: parseFloat(qtdSel),
      materiais: c.materiais || [],
    }]);
    setCompSel("");
    setQtdSel("");
  };
  const removeServico = (i) => setServicos(servicos.filter((_, ix) => ix !== i));

  const save = async () => {
    if (!form.contratante) { toast.error("Informe o contratante"); return; }
    try {
      const payload = { ...form, valor: parseFloat(form.valor) || 0 };
      let osId;
      if (editId) {
        await api.put(`/ordens/${editId}`, payload);
        osId = editId;
      } else {
        const { data } = await api.post("/ordens", payload);
        osId = data.id;
      }
      // Criar itens/serviços vinculados
      for (const s of servicos) {
        await api.post("/itens", {
          os_id: osId,
          composicao_id: s.composicao_id,
          categoria: s.categoria,
          descricao: s.descricao,
          unidade: s.unidade,
          cor: s.cor,
          qtd_planejada: s.qtd_planejada,
          materiais: s.materiais,
          situacao: "Pendente",
        });
      }
      toast.success(editId ? "OS atualizada" : `OS criada com ${servicos.length} serviço${servicos.length !== 1 ? "s" : ""}`);
      setModal(false);
      setServicos([]);
      load();
      if (!editId && servicos.length > 0) {
        nav(`/ordens/${osId}`);
      }
    } catch (e) {
      toast.error("Erro ao salvar OS");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Enviar OS para a lixeira?")) return;
    await api.delete(`/ordens/${id}`);
    toast.success("OS movida para lixeira");
    load();
  };

  const statusOptions = ["Pendente", "Em Execução", "Concluída", "Cancelada"];
  const statusCounts = statusOptions.reduce((acc, s) => {
    acc[s] = ordens.filter((o) => o.status === s).length;
    return acc;
  }, {});

  const fEmp = useFiltroEmpresa(ordens);
  const filtered = fEmp.lista.filter((o) => {
    const matchTexto = !filter ||
      o.numero?.toLowerCase().includes(filter.toLowerCase()) ||
      o.contratante?.toLowerCase().includes(filter.toLowerCase()) ||
      o.rodovia?.toLowerCase().includes(filter.toLowerCase());
    const matchStatus = !statusFilter || o.status === statusFilter;
    const matchContrato = !contratoFilter || (contratoFilter === "__sem__" ? !o.contrato_id : o.contrato_id === contratoFilter);
    return matchTexto && matchStatus && matchContrato;
  });
  const contratoCounts = ordens.reduce((acc, o) => { const k = o.contrato_id || "__sem__"; acc[k] = (acc[k] || 0) + 1; return acc; }, {});
  const contratoSel = contratos.find((c) => c.id === contratoFilter);

  return (
    <>
      <PageHeader
        testid="page-ordens"
        title="Ordens de Serviço"
        subtitle={`${filtered.length} de ${ordens.length} ordens${statusFilter ? " · " + statusFilter : ""}${contratoSel ? " · contrato " + contratoSel.numero : contratoFilter === "__sem__" ? " · sem contrato" : ""}`}
        actions={
          <>
            <input className="input" placeholder="Buscar por nº, contratante, rodovia…"
              value={filter} onChange={(e) => setFilter(e.target.value)} style={{ minWidth: 260 }}
              data-testid="os-search"
            />
            <button className="btn btn-primary" onClick={openNew} data-testid="btn-new-os">
              <Plus size={16} weight="bold" /> Nova OS
            </button>
          </>
        }
      />
      <div className="page-body">
        {/* Chips de filtro por status */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20, alignItems: "center" }} data-testid="os-status-filters">
          <span className="overline" style={{ color: "#71717A", marginRight: 4 }}>Filtrar:</span>
          <button
            className="chip-btn"
            data-active={statusFilter === ""}
            onClick={() => setStatusFilter("")}
            data-testid="filter-todas"
          >
            Todas <span className="chip-count">{ordens.length}</span>
          </button>
          {statusOptions.map((s) => (
            <button
              key={s}
              className="chip-btn"
              data-status={s}
              data-active={statusFilter === s}
              onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
              data-testid={`filter-${s.toLowerCase().replace(/\s/g, "-").replace("ç","c").replace("ã","a").replace("ó","o").replace("é","e")}`}
            >
              {s} <span className="chip-count">{statusCounts[s] || 0}</span>
            </button>
          ))}
        </div>
        {/* Filtro por contrato */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20, alignItems: "center" }} data-testid="os-contrato-filters">
          <span className="overline" style={{ color: "#71717A", marginRight: 4 }}>Contrato:</span>
          <button className="chip-btn" data-active={contratoFilter === ""} onClick={() => setContratoFilter("")} data-testid="filter-contrato-todos">Todos</button>
          <SemVinculoToggle filtro={fEmp} rotulo="OS" />
          {contratos.filter((c) => contratoCounts[c.id]).map((c) => (
            <button key={c.id} className="chip-btn" data-active={contratoFilter === c.id} onClick={() => setContratoFilter(contratoFilter === c.id ? "" : c.id)} title={c.contratante} data-testid={`filter-contrato-${c.id}`}>
              {c.numero} <span className="chip-count">{contratoCounts[c.id]}</span>
            </button>
          ))}
          {contratoCounts.__sem__ > 0 && (
            <button className="chip-btn" data-active={contratoFilter === "__sem__"} onClick={() => setContratoFilter(contratoFilter === "__sem__" ? "" : "__sem__")} data-testid="filter-contrato-sem">
              Sem contrato <span className="chip-count">{contratoCounts.__sem__}</span>
            </button>
          )}
        </div>
        {filtered.length === 0 ? (
          <EmptyState
            title="Nenhuma OS cadastrada"
            description="Crie sua primeira ordem de serviço para começar."
            action={<button className="btn btn-primary" onClick={openNew}><Plus size={16} weight="bold" /> Nova OS</button>}
          />
        ) : (
          <div className="pcp-card" style={{ overflowX: "auto" }}>
            <table className="pcp-table" data-testid="os-table">
              <thead>
                <tr>
                  <th>Nº OS</th>
                  <th>Contratante</th>
                  <th>Rodovia · Trecho</th>
                  <th>Serviços</th>
                  <th>Prazo</th>
                  <th style={{ width: 160 }}>Avanço</th>
                  <th>Status</th>
                  <th style={{ width: 120, textAlign: "right" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id}>
                    <td className="font-mono font-semibold">
                      <Link to={`/ordens/${o.id}`} style={{ color: "#0A0A0A", textDecoration: "none" }}>
                        {o.numero}
                      </Link>
                    </td>
                    <td>{o.contratante}</td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <MapPin size={14} weight="bold" color="#71717A" />
                        <span>{o.rodovia || "—"} {o.trecho && `· ${o.trecho}`}</span>
                      </div>
                    </td>
                    <td>
                      {o.total_itens > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                            {(o.categorias || []).map((c) => <span key={c} className="chip">{c}</span>)}
                          </div>
                          <span style={{ fontSize: "0.7rem", color: "#71717A" }}>{o.total_itens} item{o.total_itens !== 1 ? "s" : ""}</span>
                        </div>
                      ) : (
                        <span style={{ fontSize: "0.75rem", color: "#DC2626" }}>Sem serviços</span>
                      )}
                    </td>
                    <td className="font-mono">{formatDate(o.prazo_conclusao)}</td>
                    <td><Progress value={o.avanco || 0} /></td>
                    <td><StatusBadge status={o.status} /></td>
                    <td style={{ textAlign: "right" }}>
                      <div style={{ display: "inline-flex", gap: 4 }}>
                        <Link to={`/ordens/${o.id}`} className="btn btn-ghost btn-sm" data-testid={`os-view-${o.numero}`}><Eye size={14} weight="bold" /></Link>
                        <button className="btn btn-ghost btn-sm" onClick={() => openEdit(o)}><Pencil size={14} weight="bold" /></button>
                        <button className="btn btn-ghost btn-sm" onClick={() => remove(o.id)}><TrashSimple size={14} weight="bold" color="#DC2626" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={modal} onClose={() => setModal(false)} title={editId ? "Editar OS" : "Nova Ordem de Serviço"} size="lg">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <F label="Contrato">
            <select className="select" value={form.contrato_id || ""} onChange={(e) => setForm({ ...form, contrato_id: e.target.value })} data-testid="os-contrato">
              <option value="">— Sem contrato —</option>
              {contratos.map((c) => <option key={c.id} value={c.id}>{c.numero} · {c.contratante}</option>)}
            </select>
          </F>
          <F label="Contratante *">
            <input className="input" value={form.contratante} onChange={(e) => setForm({ ...form, contratante: e.target.value })} data-testid="os-contratante" required />
          </F>
          <F label="Tipo de contrato">
            <select className="select" value={form.tipo_contrato} onChange={(e) => setForm({ ...form, tipo_contrato: e.target.value })}>
              <option value="Público">Público</option><option value="Privado">Privado</option><option value="Concessão">Concessão</option>
            </select>
          </F>
          <F label="Equipe responsável">
            <select className="select" value={form.equipe_id || ""} onChange={(e) => setForm({ ...form, equipe_id: e.target.value })}>
              <option value="">— Sem equipe —</option>
              {equipes.map((eq) => <option key={eq.id} value={eq.id}>{eq.nome}</option>)}
            </select>
          </F>
          <F label="Empresa executora (CNPJ)">
            <select className="select" value={form.empresa_executora_id || ""} onChange={(e) => setForm({ ...form, empresa_executora_id: e.target.value })} data-testid="os-empresa-executora">
              <option value="">— Selecione a empresa executora —</option>
              {empresas.map((emp) => <option key={emp.id} value={emp.id}>{emp.nome} · {emp.cnpj}</option>)}
            </select>
          </F>
          <F label="Rodovia">
            <input className="input" value={form.rodovia} onChange={(e) => setForm({ ...form, rodovia: e.target.value })} placeholder="BR-116" />
          </F>
          <F label="Trecho">
            <input className="input" value={form.trecho} onChange={(e) => setForm({ ...form, trecho: e.target.value })} placeholder="km 100 – 200" />
          </F>
          <F label="Município">
            <input className="input" value={form.municipio} onChange={(e) => setForm({ ...form, municipio: e.target.value })} />
          </F>
          <F label="Previsão de início">
            <input className="input" type="date" value={form.data_inicio_producao || ""} onChange={(e) => setForm({ ...form, data_inicio_producao: e.target.value })} data-testid="os-inicio-producao" />
          </F>
          <F label="Previsão de término">
            <input className="input" type="date" value={form.prazo_conclusao || ""} onChange={(e) => setForm({ ...form, prazo_conclusao: e.target.value })} data-testid="os-prazo" />
          </F>
          <F label="Status">
            <select className="select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {["Pendente", "Em Execução", "Paralisada", "Concluída", "Cancelada"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </F>
          <F label="Link Google Drive" full>
            <input className="input" value={form.google_drive_link} onChange={(e) => setForm({ ...form, google_drive_link: e.target.value })} placeholder="https://drive.google.com/…" />
          </F>
          <F label="Programação automática" full>
            <div style={{ padding: 12, background: "#FAFAFA", border: "1px solid #E4E4E7", borderRadius: 3, fontSize: "0.82rem", display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ color: "#52525B" }}>
                Ao salvar com <strong>Equipe</strong>, <strong>Previsão de início</strong> e <strong>Previsão de término</strong> preenchidas,
                a OS é agendada automaticamente na Programação Semanal em todos os dias úteis do período.
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.incluir_sabado} onChange={(e) => setForm({ ...form, incluir_sabado: e.target.checked })} data-testid="os-sab" />
                  Incluir sábados
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <input type="checkbox" checked={form.incluir_domingo} onChange={(e) => setForm({ ...form, incluir_domingo: e.target.checked })} data-testid="os-dom" />
                  Incluir domingos
                </label>
              </div>
            </div>
          </F>
          <F label="Observações" full>
            <textarea className="textarea" value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} rows={3} />
          </F>
        </div>

        {!editId && (
          <div style={{ marginTop: 24, padding: 16, background: "#FFFCEA", border: "1px solid #FFCC00", borderRadius: 3 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Flask size={18} weight="bold" />
              <div className="overline" style={{ color: "#0A0A0A", margin: 0 }}>Serviços a executar (tipo de serviço)</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr auto", gap: 8, alignItems: "flex-end" }}>
              <div>
                <div className="label">Tipo de serviço (composição)</div>
                <select className="select" value={compSel} onChange={(e) => setCompSel(e.target.value)} data-testid="os-comp-sel">
                  <option value="">— Selecione o tipo de serviço —</option>
                  {["Horizontal", "Vertical", "Defensas", "Tachas", "Demarcação", "Outros"].map((cat) => {
                    const opts = composicoes.filter((c) => c.categoria === cat);
                    if (opts.length === 0) return null;
                    return (
                      <optgroup key={cat} label={cat}>
                        {opts.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.descricao} ({c.unidade}){c.cor ? ` · ${c.cor}` : ""}
                          </option>
                        ))}
                      </optgroup>
                    );
                  })}
                </select>
              </div>
              <div>
                <div className="label">Quantidade</div>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  placeholder={compSel ? composicoes.find((c) => c.id === compSel)?.unidade : ""}
                  value={qtdSel}
                  onChange={(e) => setQtdSel(e.target.value)}
                  data-testid="os-qtd-sel"
                />
              </div>
              <button className="btn btn-dark" onClick={addServico} data-testid="btn-add-servico" style={{ marginBottom: 0 }}>
                <Plus size={14} weight="bold" /> Adicionar
              </button>
            </div>

            {servicos.length > 0 && (
              <div style={{ marginTop: 16, background: "#FFFFFF", border: "1px solid #E4E4E7", borderRadius: 3 }}>
                <table className="pcp-table" style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th>Categoria</th>
                      <th>Serviço</th>
                      <th style={{ textAlign: "right" }}>Quantidade</th>
                      <th style={{ textAlign: "right" }}>Materiais</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {servicos.map((s, i) => (
                      <tr key={i}>
                        <td><span className="chip">{s.categoria}</span></td>
                        <td>{s.descricao}{s.cor ? ` · ${s.cor}` : ""}</td>
                        <td className="font-mono font-semibold" style={{ textAlign: "right" }}>
                          {s.qtd_planejada} {s.unidade}
                        </td>
                        <td style={{ textAlign: "right", fontSize: "0.75rem", color: "#52525B" }}>
                          {(s.materiais || []).length > 0 ? `${s.materiais.length} insumo${s.materiais.length !== 1 ? "s" : ""}` : "—"}
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => removeServico(i)}>
                            <X size={12} weight="bold" color="#DC2626" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {servicos.length === 0 && (
              <div style={{ marginTop: 12, fontSize: "0.78rem", color: "#52525B" }}>
                Adicione ao menos um serviço agora ou pule esta etapa e adicione depois na tela da OS.
              </div>
            )}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
          <button className="btn btn-outline" onClick={() => setModal(false)}>Cancelar</button>
          <button className="btn btn-primary" onClick={save} data-testid="btn-save-os">
            {editId ? "Salvar" : servicos.length > 0 ? `Criar OS com ${servicos.length} serviço${servicos.length !== 1 ? "s" : ""}` : "Criar OS"}
          </button>
        </div>
      </Modal>
    </>
  );
}

function F({ label, children, full }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : undefined }}>
      <div className="label">{label}</div>
      {children}
    </div>
  );
}
